import LoadingScreen from "minimals-template/components/LoadingScreen"
import { Bound } from "lib/@components/binding/Bound"
import { Box, Stack } from "@mui/material"
import { NoResults } from "lib/@components/no-results/no-results"
import { useParams } from "react-router"
import { getProjectSchedules } from "../../controller/get-project-schedules"
import { useCallback, useEffect, useMemo, useState } from "react"
import { ResetTabledRepeat } from "event-definitions"
import { refreshOnlyFor } from "lib/graphql/api"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { AddGroup } from "../plugins/projectToolbar/add-group"
import { GroupTable } from "./group-table"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { isLockedToLegacy } from "authoring/lib/enum/project-phase.mjs"
import GenericSearch from "lib/@components/paging/generic-search"

export function EditGroupsTab() {
    const { projectId } = useParams()
    const { target: project } = useBoundContext()
    const projectSchedules = getProjectSchedules.useResults(
        { projectId },
        refreshOnlyFor(projectId),
        withInitialValue(IS_LOADING)
    )
    const schedulesLoading = projectSchedules === IS_LOADING
    const projectGroups = useMemo(() => project?.groups ?? [], [project?.groups])
    const [search, setSearch] = useState("")

    const matchesSearch = useCallback(matchSearch, [search])
    const filteredGroups = useMemo(getFilteredGroups, [matchesSearch, projectGroups])

    useEffect(() => {
        ResetTabledRepeat.raiseOnce()
    }, [projectGroups, projectSchedules, search])

    if (schedulesLoading) return <LoadingScreen />

    const sentToLegacy = isLockedToLegacy(project.phase)
    return (
        <Bound projectSchedules={projectSchedules} projectGroups={filteredGroups}>
            <Box flex={1}>
                <Stack direction="row" sx={{ justifyContent: "space-between", mb: 3 }}>
                    <AddGroup variant="contained" disabled={!sentToLegacy} />
                    <GenericSearch
                        results={projectGroups}
                        searchPlaceHolder="Search"
                        search={search}
                        setSearch={setSearch}
                        showResultCount={false}
                        size="small"
                    />
                </Stack>
                <If truthy={projectGroups?.length > 0}>
                    <If truthy={filteredGroups?.length > 0}>
                        <GroupTable />
                        <Else>
                            <Stack spacing={2} sx={{ alignItems: "center" }}>
                                <NoResults>{"No groups match your search."}</NoResults>
                            </Stack>
                        </Else>
                    </If>
                    <Else>
                        <Stack spacing={2} sx={{ alignItems: "center" }}>
                            <NoResults>
                                {"You don't have any groups in this work package yet. Start by adding a group."}
                            </NoResults>
                        </Stack>
                    </Else>
                </If>
            </Box>
        </Bound>
    )

    function matchSearch({ name }) {
        const searchLower = search.trim().toLowerCase()
        if (!searchLower) return true
        return name?.toLowerCase()?.includes(searchLower)
    }

    function getFilteredGroups() {
        return projectGroups?.filter(matchesSearch)
    }
}
